'use client';

import Link from 'next/link';
import DeleteButton from '@/app/components/ui/DeleteButton';
import { Thumbnail } from '@/app/saved/components/Thumbnail';
import { TYPE_META, badgeLabel, openHref, isOlderVersion, type LibraryItem } from '@/app/saved/lib/library';
import { useVersion } from '@/app/context/VersionContext';
import SectionText from '@/app/components/minecraft/SectionText';
import { stripCodes } from '@/app/lib/minecraft/formatCodes';

const dateLabel = (iso: string) => {
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return new Date(iso).toLocaleDateString('en-US');
};

const FolderIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-2.5 h-2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
  </svg>
);

export interface LibraryCardProps {
  item: LibraryItem;
  view: 'grid' | 'list';
  /** In the All view we flag which folder a build lives in; inside a folder it's redundant. */
  showFolder: boolean;
  copied: boolean;
  onCopyShare: (item: LibraryItem) => void;
  onDelete: (item: LibraryItem) => void;
  onTagClick: (tag: string) => void;
  /** Open the quick-look / edit modal (Phase 2). When omitted the body is not clickable. */
  onOpenModal?: (item: LibraryItem) => void;
  /** Loading a non-player skin needs its base64 stashed for the editor to pick up. */
  onOpenTool?: (item: LibraryItem) => void;
  /** Drag-to-folder (element items only). */
  onDragStartItem?: (item: LibraryItem) => void;
  onDragEndItem?: () => void;
  dragging?: boolean;
  /** Bulk-select mode. */
  selecting?: boolean;
  selected?: boolean;
  onToggleSelect?: (item: LibraryItem) => void;
}

export function LibraryCard({
  item, view, showFolder, copied, onCopyShare, onDelete, onTagClick, onOpenModal, onOpenTool,
  onDragStartItem, onDragEndItem, dragging, selecting, selected, onToggleSelect,
}: LibraryCardProps) {
  const def = TYPE_META[item.type];
  const list = view === 'list';
  const draggable = item.source === 'element' && !!onDragStartItem && !selecting;
  const handleCardClick = selecting && onToggleSelect
    ? () => onToggleSelect(item)
    : onOpenModal ? () => onOpenModal(item) : undefined;

  // Only version-faithful ELEMENT builds carry the older-version treatment; favorites (and the
  // version-less skin/profile reference items) never do — matching openHref, which won't version them.
  const { version: working } = useVersion();
  const old = item.source === 'element' && isOlderVersion(item.version, working);

  const openLink = (
    <Link
      href={openHref(item)}
      onClick={e => { e.stopPropagation(); onOpenTool?.(item); }}
      className={`flex-1 text-center text-[11px] px-2 py-1.5 rounded transition-colors inline-flex items-center justify-center gap-1 ${def.tint}`}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" d="M13 7l5 5-5 5M6 12h12" /></svg>
      {item.buttonLabel}
    </Link>
  );

  return (
    <div
      className={`group relative bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden shadow-sm transition hover:border-zinc-700 hover:-translate-y-0.5 focus-within:border-zinc-700 ${
        list ? 'flex items-stretch' : 'flex flex-col'
      } ${handleCardClick ? 'cursor-pointer' : ''} ${draggable ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging ? 'opacity-35' : ''} ${selected ? 'outline outline-2 -outline-offset-2 outline-emerald-500' : ''}`}
      onClick={handleCardClick}
      tabIndex={0}
      onKeyDown={handleCardClick ? e => { if (e.key === 'Enter') handleCardClick(); } : undefined}
      draggable={draggable}
      onDragStart={draggable ? e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', item.uid); onDragStartItem!(item); } : undefined}
      onDragEnd={draggable ? () => onDragEndItem?.() : undefined}
    >
      {/* thumbnail */}
      <div className={`relative overflow-hidden ${list ? 'w-28 shrink-0' : 'aspect-[16/10]'}`}>
        {selecting ? (
          <span className={`absolute top-2 left-2 z-[3] w-5 h-5 rounded-md border-2 flex items-center justify-center ${selected ? 'bg-emerald-500 border-emerald-500 text-zinc-950' : 'bg-zinc-950/60 border-white/50'}`}>
            {selected && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="w-3 h-3"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>}
          </span>
        ) : (
          <span className="absolute top-2 left-2 z-[2] flex items-center gap-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap bg-zinc-950/70 backdrop-blur border border-white/5 text-zinc-200">
            <span className="w-1.5 h-1.5 shrink-0 rounded-sm" style={{ background: def.hex }} />
            {badgeLabel(item.type)}
          </span>
        )}
        {showFolder && item.folder && (
          <span className="absolute top-2 right-2 z-[2] max-w-[45%] flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-950/70 backdrop-blur border border-violet-400/25 text-violet-300" title={`In folder: ${item.folder}`}>
            <span className="shrink-0">{FolderIcon}</span><span className="min-w-0 truncate">{item.folder}</span>
          </span>
        )}
        {item.pinned && (
          <span className="absolute bottom-2 right-2 z-[2] text-amber-400 drop-shadow" title="Pinned">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M9 4h6l-1 7 4 3v2H6v-2l4-3z" /><rect x="11" y="16" width="2" height="5" rx="1" /></svg>
          </span>
        )}
        <Thumbnail item={item} size={list ? 'list' : 'grid'} />
      </div>

      {/* body */}
      <div className={`p-3 flex flex-col gap-1.5 min-w-0 ${list ? 'flex-1 justify-center' : ''}`}>
        <div dir="auto" className="text-[13px] font-semibold text-zinc-100 leading-tight truncate" title={stripCodes(item.name, { sectionOnly: true })}><SectionText text={item.name} /></div>
        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
          {item.version && (
            old ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-mono tabular-nums text-[10.5px]" title={`Made for ${item.version} — older than your ${working}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-2.5 h-2.5"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 7v5l3 2" /></svg>
                {item.version}
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 font-mono tabular-nums text-[10.5px]">{item.version}</span>
            )
          )}
          <span>{dateLabel(item.createdAt)}</span>
        </div>
        {item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 3).map(tg => (
              <button
                key={tg}
                onClick={e => { e.stopPropagation(); onTagClick(tg); }}
                className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-800 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 transition-colors leading-relaxed"
              >
                {tg}
              </button>
            ))}
            {item.tags.length > 3 && <span className="text-[10px] px-1 py-0.5 text-zinc-500">+{item.tags.length - 3}</span>}
          </div>
        )}

        <div className={`flex items-center gap-1.5 mt-0.5 transition-opacity ${selecting ? 'hidden' : list ? '' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100'}`}>
          {openLink}
          <button
            onClick={e => { e.stopPropagation(); onCopyShare(item); }}
            className={`shrink-0 w-8 h-7 flex items-center justify-center rounded border border-zinc-800 bg-zinc-800/60 transition-colors ${copied ? 'text-emerald-400 border-emerald-500/40' : 'text-zinc-400 hover:text-violet-400 hover:border-violet-400/40'}`}
            title={copied ? 'Copied!' : 'Copy share link'}
          >
            {copied
              ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
              : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4M18 8a3 3 0 10-3-3 3 3 0 003 3zM6 15a3 3 0 103-3 3 3 0 00-3 3zM18 22a3 3 0 10-3-3 3 3 0 003 3z" /></svg>}
          </button>
          <div onClick={e => e.stopPropagation()} className="shrink-0 flex items-center justify-center w-8 h-7 rounded border border-zinc-800 bg-zinc-800/60">
            <DeleteButton onDelete={() => onDelete(item)} compact />
          </div>
        </div>
      </div>
    </div>
  );
}
